/**
 * A message's UI state, as this client tracks it through a turn.
 *
 * Client-owned on purpose: the frontend assigns every one of these — `idle` and `thinking` when a
 * message enters the cache, `blocked` / `unauthenticated` / `error` from the stream handler,
 * `cancelled` from the stop button, and the rest from the streamed `status` event. The server has
 * no counterpart and never sent one; it used to be inherited from a BFF enum on a persisted field
 * that was always `idle`.
 *
 * Not the same vocabulary as the streamed `status` event, which is server-owned and narrower.
 */
export type AssistantStatus =
    | "idle"
    | "thinking"
    | "assistant_message"
    | "writing"
    | "searching"
    | "tool_running"
    | "end_of_stream"
    | "error"
    | "blocked"
    | "file_processing"
    | "cancelled"
    | "unauthenticated"
    | "processing_page_action"
    | "compacting";

/**
 * Set of assistant statuses that indicate the assistant has finished generating a response.
 * These are "terminal" states where no further generation will occur.
 */
export const TERMINAL_STATUSES = new Set<AssistantStatus>([
    "idle",
    "error",
    "blocked",
    "unauthenticated",
    "cancelled",
    "end_of_stream"
]);

/**
 * Determines if the assistant is currently generating a response.
 * @param status - The current assistant status
 * @returns true if the assistant is generating, false if it has reached a terminal state
 */
export const isGenerating = (status: AssistantStatus): boolean => {
    return !TERMINAL_STATUSES.has(status);
};

/**
 * Determines if the assistant stream was interrupted (error or cancelled).
 * @param status - The current assistant status
 * @returns true if the stream was interrupted, false otherwise
 */
export const isInterrupted = (status: AssistantStatus): boolean => {
    return status === "error" || status === "cancelled";
};
